import { useTranslation } from "react-i18next";
import type { PromptPart } from "../../../api/threads/promptPartsApi";
import type { ThreadStepPlan } from "../../../api/threads/threadsApi";
import { spaced, useItemFacts } from "../../workflows";
import FilledText from "../stepPlans/FilledText";
import type { GivenValues } from "../stepPlans/PlannedInputsList";
import { partItemReference, promptPartTitle } from "../stepPlans/promptPartWords";
import { usePromptParts } from "../stepPlans/usePromptParts";

const NO_VALUES: GivenValues = {};

interface RunStepPartsProps {
  readonly threadId: string;
  readonly projectId: string;
  /** The step's plan in the Run; an agent step's has a prompt to fill. */
  readonly plan: ThreadStepPlan | undefined;
  /** It has not run yet, so its prompt is filled as its run would fill it now. */
  readonly beforeItRuns: boolean;
  readonly lastEntryNumber: number;
}

/**
 * On the run graph, an open agent step card's parts before it runs (F148: "in the instatiatedgraph to be able to see
 * what things were exactly filledi n with inputs that i coudl expand"): each part its prompt fills itself, by the name
 * its card in the Run view has, opening to its text. Read when the card opens. A step that ran shows what it sent in
 * the Run, which its state opens; this doesn't fill it again.
 */
export default function RunStepParts({ threadId, projectId, plan, beforeItRuns, lastEntryNumber }: RunStepPartsProps) {
  const { t } = useTranslation();
  const agent = plan !== undefined && plan.stepKind === "agent" && beforeItRuns;
  const parts = usePromptParts(threadId, agent ? plan.stepKey : null, NO_VALUES, lastEntryNumber);
  const items = useItemFacts(projectId, (parts?.parts ?? []).flatMap((part) => partItemReference(part) ?? []));
  if (parts === null || parts.parts.length === 0) return null;
  const titleOf = (part: PromptPart) => {
    if (part.input !== null) return plan?.inputs.find((input) => input.name === part.input)?.label ?? spaced(part.input);
    const reference = partItemReference(part);
    return promptPartTitle(part, reference === null ? null : (items.get(reference)?.name ?? null), t);
  };
  return (
    // A row of the card's own list, so it scrolls with the card's lines (taste round, 3 Nov, shot de-6).
    <div className="contents">
      <dt>{t("workflows.details.labels.filled")}</dt>
      <dd>
        <ul>
          {parts.parts.map((part) => (
            <li key={part.name}>
              {part.text === null ? (
                <span>
                  {titleOf(part)} <span className="app-text-muted">{t("threads.plan.parts.notFilled")}</span>
                </span>
              ) : (
                <FilledText summary={titleOf(part)} text={part.text} />
              )}
            </li>
          ))}
        </ul>
      </dd>
    </div>
  );
}
